import { useState, useEffect, useCallback } from 'react';
import Navbar         from '../components/layout/Navbar';
import AlertBanner    from '../components/layout/AlertBanner';
import KpiCards       from '../components/dashboard/KpiCards';
import ServerSection  from '../components/dashboard/ServerSection';
import ServerDrawer   from '../components/dashboard/ServerDrawer';
import ServiceSection from '../components/dashboard/ServiceSection';
import WebsiteSection from '../components/dashboard/WebsiteSection';
import MetricPanel    from '../components/common/MetricPanel';
import LoadingScreen  from '../components/common/LoadingScreen';

import {
  fetchMonitoringStatusServeurDieseData,
  fetchMonitoringServicesData,
  fetchMonitoringWebsiteData,
} from '../api/api';

const REFRESH_INTERVAL = 30000;

const pageStyle = {
  minHeight:  '100vh',
  background: 'var(--bg)',
};

const contentStyle = {
  display:       'flex',
  flexDirection: 'column',
  gap:           '2rem',
  paddingBottom: '3rem',
  overflowX:     'hidden',
};

const errorStyle = {
  margin:        '2rem',
  padding:       '1.25rem',
  borderRadius:  'var(--radius-md)',
  background:    'var(--crit-bg)',
  border:        '1px solid var(--crit-border)',
  color:         'var(--crit-text)',
  fontSize:      '0.88rem',
};

function Home({ navigateTo, user, onLogout }) {
  const [servers,      setServers]      = useState(null);
  const [services,     setServices]     = useState(null);
  const [websites,     setWebsites]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState(null);
  const [lastUpdate,   setLastUpdate]   = useState(null);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [drawerServer, setDrawerServer] = useState(null);
  const [metricPanel,  setMetricPanel]  = useState({ open: false, server: null, metric: null });

  const loadAll = useCallback(async (page = 1, silent = false) => {
    if (!silent) { setLoading(true); setError(null); }

    try {
      const [serveurData, serviceData, websiteData] = await Promise.all([
        fetchMonitoringStatusServeurDieseData(),
        fetchMonitoringServicesData(),
        fetchMonitoringWebsiteData(page, 20),
      ]);
      setServers(serveurData);
      setServices(serviceData);
      setWebsites(websiteData);
      setLastUpdate(new Date());
    } catch (err) {
      if (!silent) setError('Erreur lors du chargement des données.');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(currentPage, false); }, [loadAll, currentPage]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [sd, svc, web] = await Promise.all([
          fetchMonitoringStatusServeurDieseData(),
          fetchMonitoringServicesData(),
          fetchMonitoringWebsiteData(currentPage, 20),
        ]);
        setServers(sd);
        setServices(svc);
        setWebsites(web);
        setLastUpdate(new Date());
      } catch (err) {
        console.error('Auto-refresh error:', err);
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [currentPage]);

  function handleRefresh() { setRefreshing(true); loadAll(currentPage, true); }
  function openMetricPanel(server, metric) { setMetricPanel({ open: true, server, metric }); }
  function closeMetricPanel()              { setMetricPanel({ open: false, server: null, metric: null }); }

  if (loading) {
    return (
      <div style={pageStyle}>
        <Navbar lastUpdate={lastUpdate} onRefresh={handleRefresh} refreshing={refreshing} user={user} onLogout={onLogout} />
        <LoadingScreen />
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <Navbar lastUpdate={lastUpdate} onRefresh={handleRefresh} refreshing={refreshing} user={user} onLogout={onLogout} />
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Navbar lastUpdate={lastUpdate} onRefresh={handleRefresh} refreshing={refreshing} user={user} onLogout={onLogout} />

      <AlertBanner servers={servers} services={services} websites={websites} />

      <div style={contentStyle}>
        <KpiCards servers={servers} services={services} websites={websites} />

        <ServerSection
          servers={servers}
          onMetricClick={openMetricPanel}
          onOpenDrawer={setDrawerServer}
        />

        <ServiceSection services={services} />

        <WebsiteSection
          websites={websites}
          onPageChange={setCurrentPage}
          onClientAdded={() => loadAll(currentPage, true)}
        />
      </div>

      <ServerDrawer server={drawerServer} onClose={() => setDrawerServer(null)} />

      <MetricPanel
        open={metricPanel.open}
        server={metricPanel.server}
        metric={metricPanel.metric}
        onClose={closeMetricPanel}
      />
    </div>
  );
}

export default Home;
